import { getCompanyDataByDomain, getCompanyLoginMethodsByUrl } from '@api/company';
import { LoginTypes, loginUser } from '@api/login';
import FormButtonNew from '@components/FormButtonNew/FormButtonNew';
import LabelCheckbox from '@components/Molecules/LabelCheckbox';
import LabelInput from '@components/Molecules/LabelInput';
import { LabelPositions } from '@enums/LabelPositions';
import { LoginMethods } from '@enums/LoginMethods';
import { buildCheckYourInboxPath } from '@helpers/auth.helper';
import { emailValidation, getEmailInputValue } from '@helpers/validation.helper';
import useInput from '@hooks/useInput';
import useIsMobileApp from '@hooks/useIsMobileApp';
import { CompanyLoginMethodTypesInterface, CompanyLoginMethodsInterface } from '@interfaces/index';
import { CircularProgress } from '@mui/material';
import { showToast } from '@shared/atoms';
import { dismissToast } from '@shared/atoms/ToastProvider/ToastProvider';
import { ToastMessages } from 'constants/toastMessages';
import { castResponse } from 'custom.d';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

const LoginForm: React.FunctionComponent<LoginFormProps> = (props) => {
  const { organizationId, companyURL, handleLoginMethodsToDisplay, setRememberMe, rememberMe } =
    props;
  const history = useHistory();
  const isMobileApp = useIsMobileApp();
  const [isLoading, setIsLoading] = useState(false);

  const {
    value: emailValue,
    isValid: emailIsValid,
    hasError: emailHasError,
    valueChangeHandler: emailChangeHandler,
    inputBlurHandler: emailBlurHandler,
    reset: emailReset,
  } = useInput(emailValidation, getEmailInputValue, '');

  const sendLoginMagicRequest = async () => {
    const type = isMobileApp ? LoginTypes.TOKEN : LoginTypes.LINK;

    try {
      const response = await loginUser({
        email: emailValue.toLowerCase(),
        type,
        organizationId,
        remember: rememberMe,
      });

      if (response.ok) {
        showToast(ToastMessages.auth.info.EMAIL_SENT_TO_YOUR_INBOX, undefined, 'info');
        navigateToCheckInbox();
      } else if (response.status === 400 || response.status === 403) {
        showToast(ToastMessages.auth.info.WE_COULD_NOT_RECOGNISE_THIS_EMAIL, undefined, 'warning');
      } else {
        showToast(ToastMessages.auth.error.LOGIN_UNSUCCESSFUL_CONTACT_ADMIN, undefined, 'error');
      }
    } catch (error) {
      showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToCheckInbox = () => {
    history.push(
      buildCheckYourInboxPath(emailValue, {
        companyURL,
        isMobileApp,
      }),
    );
  };

  const handleSingleLoginMethod = async (loginMethod: CompanyLoginMethodTypesInterface) => {
    const isSSO = loginMethod?.loginMethod === LoginMethods.SSO;

    if (isSSO) {
      await showToast(ToastMessages.auth.info.REDIRECTING_TO_SINGLE_SIGN_ON, undefined, 'info');
      window.location.replace(loginMethod?.loginUrl);
    } else {
      await sendLoginMagicRequest();
    }
  };

  const handleMultipleLoginMethods = (loginMethods: any[]) => {
    handleLoginMethodsToDisplay(loginMethods, emailValue);
  };

  const handleNoLoginMethods = () => {
    handleLoginMethodsToDisplay([], emailValue);
    sendLoginMagicRequest();
  };

  const processCompanyLoginMethods = async (companyUrl: string) => {
    try {
      const companyLoginMethods = await getCompanyLoginMethodsByUrl(companyUrl);
      const companyLoginMethodsCast =
        castResponse<CompanyLoginMethodsInterface>(companyLoginMethods);

      if (!companyLoginMethods?.success || !companyLoginMethodsCast) {
        return sendLoginMagicRequest();
      }

      const loginMethodsCount = companyLoginMethodsCast?.companyLoginMethods?.length || 0;

      if (loginMethodsCount === 1) {
        return handleSingleLoginMethod(companyLoginMethodsCast.companyLoginMethods[0]);
      }

      if (loginMethodsCount > 1) {
        return handleMultipleLoginMethods(companyLoginMethodsCast.companyLoginMethods);
      }

      return handleNoLoginMethods();
    } catch (error) {
      return sendLoginMagicRequest();
    }
  };

  const handleCreateUser = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();

    if (!emailValue) {
      showToast(ToastMessages.auth.info.PLEASE_ENTER_A_VALID_EMAIL_ADDRESS, undefined, 'warning');

      return;
    }

    setIsLoading(true);
    const loadingToastId = showToast(
      ToastMessages.auth.info.PROCESSING_LOGIN_REQUEST,
      undefined,
      'loading',
    );

    try {
      if (isMobileApp) {
        await sendLoginMagicRequest();
      } else if (!isMobileApp) {
        if (companyURL) {
          // Handle existing company URL
          await processCompanyLoginMethods(companyURL);
        } else {
          // Try to find company by email domain
          const emailDomain = emailValue.split('@')[1];
          const companyDataByDomain = await getCompanyDataByDomain(emailDomain);

          if (companyDataByDomain?.company_url) {
            await processCompanyLoginMethods(companyDataByDomain.company_url);
          } else {
            await sendLoginMagicRequest();
          }
        }
      }
    } catch (error) {
      showToast(
        ToastMessages.auth.error.SOMETHING_WENT_WRONG_DURING_LOGIN_TRY_AGAIN,
        undefined,
        'error',
      );
      setIsLoading(false);
    } finally {
      dismissToast(loadingToastId);
      emailReset();
      setIsLoading(false);
    }
  };

  const handleRememberMe = () => {
    setRememberMe((preState: boolean) => !preState);
  };

  return (
    <form onSubmit={handleCreateUser}>
      <div className={styles.inputHolder}>
        <LabelInput
          id="email"
          label="Email *"
          type="text"
          name="email"
          placeholder="Add your email here..."
          onChange={emailChangeHandler}
          onBlur={emailBlurHandler}
          value={emailValue || ''}
          isError={emailHasError}
        />
        {emailHasError && <p className={styles.hsError}>Please enter a valid email address</p>}
      </div>

      <div className={styles.inputHolderRememberMe}>
        <LabelCheckbox
          id="rememberMe"
          name="rememberMe"
          onChange={handleRememberMe}
          checked={rememberMe}
          labelPosition={LabelPositions.right}
          label="Keep me signed in on this device"
        />
      </div>

      <div className={styles.buttonHolder}>
        <FormButtonNew
          onClick={handleCreateUser}
          disabled={(!emailIsValid && !isMobileApp) || isLoading}
          color="primary"
          type="submit"
        >
          {isLoading ? (
            <>
              <CircularProgress color="inherit" size={20} />
              &nbsp;Loading...
            </>
          ) : (
            'Login'
          )}
        </FormButtonNew>
      </div>
    </form>
  );
};

LoginForm.displayName = 'LoginForm';

LoginForm.defaultProps = {
  organizationId: 0,
  companyURL: undefined,
};

interface LoginFormProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  organizationId?: number;
  companyURL?: string;
  handleLoginMethodsToDisplay: (methods: CompanyLoginMethodTypesInterface[], email: string) => void;
  setRememberMe: any;
  rememberMe: boolean;
}

export default LoginForm;
